// Per spec/design-system.md §Primitive 7 + D-030 (rel default semantics).
//
// `rel` defaults to `'noopener noreferrer'` when target is `_blank` (the prop
// default) and is suppressed when target is `_self`. Callers can override with
// any string (e.g., `'nofollow'`); explicit values always emit regardless of
// target.
//
// **P-037.26 D-134 — `toAbsoluteUrl` applied to `href`.** Most callers
// pass an absolute URL (`https://...`) or a Constant Contact variable
// (`[[unsubscribe]]`); both pass through `toAbsoluteUrl` unchanged.
// The helper covers the edge case where a chapter rich-text richTextField
// (CTA, body link) might paste a relative path — the rendered email
// then carries an absolute URL, matching the requirement that
// Constant Contact + View-in-Tab can resolve it.

import { html, toAbsoluteUrl } from '../render'
import { tokens } from '../tokens'
import { HtmlString } from '../types'

export type LinkTarget = '_blank' | '_self'

export interface LinkProps {
  text: string
  href: string
  color?: string
  weight?: number
  underline?: boolean
  target?: LinkTarget
  rel?: string
}

export function link({
  text,
  href,
  color = tokens.color['accent-coral'],
  weight = tokens.type.weight.semibold,
  underline = false,
  target = '_blank',
  rel,
}: LinkProps): HtmlString {
  const absoluteHref = toAbsoluteUrl(href)
  const finalRel = rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)
  const decoration = underline ? 'underline' : 'none'
  // P-037.13: `font-family` added so links don't inherit the user-agent
  // default (often serif in email clients) when the wrapping `<td>` doesn't
  // set a family. The other text-emitting primitives (`heading`, `text`,
  // `button`) already set font-family inline; link was the gap. Banks's
  // audit: "There should not be any font in a Block other than Helvetica."
  const style = `font-family: ${tokens.type.fontFamily}; color: ${color}; font-weight: ${weight}; text-decoration: ${decoration};`
  // prettier-ignore
  return finalRel !== undefined
    ? html`<a href="${absoluteHref}" target="${target}" rel="${finalRel}" style="${style}">${text}</a>`
    : html`<a href="${absoluteHref}" target="${target}" style="${style}">${text}</a>`
}
